import React, { useState, useEffect } from 'react';
import { AlertTriangle, Check, X, User, Gamepad2, Plus, Loader2 } from 'lucide-react';
import MicrosoftLoginModal from './MicrosoftLoginModal';

interface MinecraftAccount {
  id: string;
  username: string;
  uuid: string;
  type: 'offline' | 'microsoft';
  accessToken?: string;
  created_at: string;
}

const AccountConnectionsTab: React.FC = () => {
  const [minecraftAccounts, setMinecraftAccounts] = useState<MinecraftAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newUsername, setNewUsername] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [isElectron] = useState(() => !!window.api);
  const [activeAccount, setActiveAccount] = useState<string | null>(null);
  const [showMicrosoftModal, setShowMicrosoftModal] = useState(false);

  useEffect(() => {
    const fetchAccounts = async () => {
      if (!window.api) { setMinecraftAccounts([]); setLoading(false); return; }
      try {
        setLoading(true);
        const accounts = await window.api.getMinecraftAccounts();
        setMinecraftAccounts(accounts || []);
        const currentUser = await window.api.getCurrentUser();
        if (currentUser.success) {
          const account = accounts.find((acc: MinecraftAccount) =>
            acc.username === currentUser.user.username && acc.uuid === currentUser.user.uuid
          );
          if (account) setActiveAccount(account.id);
        }
        setError(null);
      } catch (err) {
        console.error('Error loading Minecraft accounts:', err);
        setError('Impossible de charger vos comptes Minecraft');
      } finally {
        setLoading(false);
      }
    };
    fetchAccounts();
  }, []);

  const addOfflineAccount = async () => {
    if (!newUsername.trim() || !window.api) { setAddError('Veuillez entrer un nom d\'utilisateur'); return; }
    try {
      setIsAdding(true); setAddError(null);
      const result = await window.api.addMinecraftAccount({ username: newUsername, type: 'offline' });
      if (result.success) {
        setMinecraftAccounts(prev => {
          const existingIndex = prev.findIndex(acc => acc.id === result.account.id);
          if (existingIndex >= 0) return [...prev.slice(0, existingIndex), result.account, ...prev.slice(existingIndex + 1)];
          return [...prev, result.account];
        });
        setNewUsername('');
      } else { setAddError(result.error || 'Impossible d\'ajouter le compte'); }
    } catch (err: any) {
      setAddError(err.message || 'Impossible d\'ajouter le compte');
    } finally { setIsAdding(false); }
  };

  const removeAccount = async (accountId: string) => {
    if (!window.api) return;
    try {
      const result = await window.api.removeMinecraftAccount(accountId);
      if (result.success) {
        setMinecraftAccounts(prev => prev.filter(acc => acc.id !== accountId));
        if (activeAccount === accountId) setActiveAccount(null);
      } else { setError(result.error || 'Échec de la suppression'); }
    } catch { setError('Échec de la suppression'); }
  };

  const setActiveMinecraftAccount = async (accountId: string) => {
    if (!window.api) return;
    try {
      const result = await window.api.setActiveMinecraftAccount(accountId);
      if (result.success) setActiveAccount(accountId);
      else setError(result.error || 'Échec du changement de compte');
    } catch { setError('Échec du changement de compte'); }
  };

  if (!isElectron) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-bold text-text-primary">Connexions</h2>
          <p className="text-xs text-text-muted mt-0.5">Gérez vos connexions aux services tiers.</p>
        </div>
        <div className="flex items-center gap-3 p-4 bg-warning/10 border border-warning/30 rounded-xl">
          <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-warning">Application Desktop requise</p>
            <p className="text-xs text-text-muted mt-0.5">La gestion des comptes Minecraft nécessite l'application Erozion Desktop.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-text-primary">Connexions</h2>
        <p className="text-xs text-text-muted mt-0.5">Gérez vos connexions aux services tiers.</p>
      </div>

      {/* Minecraft section */}
      <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-5">
        <h3 className="text-sm font-bold text-text-primary uppercase tracking-wide mb-4 flex items-center gap-2">
          <Gamepad2 className="w-4 h-4 text-accent-color" /> Minecraft
        </h3>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 text-accent-color animate-spin" />
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-error/10 border border-error/30 text-error text-xs">
            <X className="w-3.5 h-3.5 flex-shrink-0" /> {error}
          </div>
        ) : (
          <>
            {/* Accounts list */}
            <div className="space-y-2 mb-5">
              {minecraftAccounts.length === 0 ? (
                <p className="text-sm text-text-muted py-2">Aucun compte Minecraft ajouté.</p>
              ) : (
                minecraftAccounts.map(account => (
                  <div
                    key={account.id}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                      activeAccount === account.id
                        ? 'bg-accent-color/5 border-accent-color/30'
                        : 'bg-bg-tertiary/50 border-border-color hover:border-text-muted/40'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        account.type === 'microsoft' ? 'bg-blue-500/10' : 'bg-bg-tertiary'
                      }`}>
                        <User className={`w-4 h-4 ${account.type === 'microsoft' ? 'text-blue-400' : 'text-text-muted'}`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-text-primary truncate">{account.username}</span>
                          {activeAccount === account.id && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-success/15 text-success uppercase tracking-wide">
                              Actif
                            </span>
                          )}
                          <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded uppercase tracking-wide ${
                            account.type === 'microsoft' ? 'bg-blue-500/15 text-blue-400' : 'bg-bg-tertiary text-text-muted'
                          }`}>
                            {account.type === 'microsoft' ? 'Microsoft' : 'Hors ligne'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {activeAccount !== account.id && (
                        <button
                          onClick={() => setActiveMinecraftAccount(account.id)}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-success hover:bg-success/10 transition-all"
                          title="Définir comme actif"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => removeAccount(account.id)}
                        className="w-7 h-7 rounded-md flex items-center justify-center text-error hover:bg-error/10 transition-all"
                        title="Supprimer"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Add account */}
            <div className="border-t border-border-color pt-4 space-y-3">
              <p className="text-xs font-bold text-text-secondary uppercase tracking-wide flex items-center gap-1.5">
                <Plus className="w-3 h-3" /> Ajouter un compte
              </p>

              {/* Microsoft */}
              <button
                onClick={() => setShowMicrosoftModal(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-accent-color text-black rounded-lg text-xs font-bold hover:bg-accent-hover transition-all"
              >
                <svg viewBox="0 0 21 21" className="w-4 h-4" fill="currentColor">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                Connexion Microsoft
              </button>
              <p className="text-[10px] text-text-muted text-center">Requis pour les serveurs officiels</p>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 border-t border-border-color" />
                <span className="text-[10px] text-text-muted">ou</span>
                <div className="flex-1 border-t border-border-color" />
              </div>

              {/* Offline */}
              <div>
                <p className="text-[10px] text-text-muted mb-2">Compte hors ligne (serveurs crackés uniquement)</p>
                {addError && (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-error/10 border border-error/30 text-error text-xs mb-2">
                    <X className="w-3 h-3 flex-shrink-0" /> {addError}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newUsername}
                    onChange={e => setNewUsername(e.target.value)}
                    placeholder="Nom d'utilisateur Minecraft"
                    className="settings-input flex-1"
                    onKeyDown={(e) => { if (e.key === 'Enter') addOfflineAccount(); }}
                  />
                  <button
                    onClick={addOfflineAccount}
                    disabled={isAdding || !newUsername.trim()}
                    className="w-10 h-10 rounded-lg bg-bg-tertiary border border-border-color flex items-center justify-center hover:bg-highlight transition-all disabled:opacity-40"
                  >
                    {isAdding ? <Loader2 className="w-3.5 h-3.5 animate-spin text-text-muted" /> : <Plus className="w-3.5 h-3.5 text-text-primary" />}
                  </button>
                </div>
              </div>
            </div>

            {showMicrosoftModal && (
              <MicrosoftLoginModal
                onSuccess={(account) => {
                  setMinecraftAccounts(prev => {
                    const idx = prev.findIndex(a => a.id === account.id);
                    if (idx >= 0) return [...prev.slice(0, idx), account, ...prev.slice(idx + 1)];
                    return [...prev, account];
                  });
                  setActiveAccount(account.id);
                  setShowMicrosoftModal(false);
                }}
                onClose={() => setShowMicrosoftModal(false)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AccountConnectionsTab;